'use client';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, FormControl, MenuItem, Select, Switch, Typography } from '@mui/material';

import { useT } from '@/hooks';

interface MobileTradeHeaderProps {
  activeTab: 'buy' | 'sell';
  onTabChange: (tab: 'buy' | 'sell') => void;
  orderType: 'market' | 'limit';
  onOrderTypeChange: (type: 'market' | 'limit') => void;
}

export function MobileTradeHeader({
  activeTab,
  onTabChange,
  orderType,
  onOrderTypeChange,
}: MobileTradeHeaderProps) {
  const { t } = useT();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Buy/Sell Dropdown */}
      <FormControl size="small" sx={{ minWidth: 80 }}>
        <Select
          value={activeTab}
          onChange={(e) => onTabChange(e.target.value as 'buy' | 'sell')}
          IconComponent={KeyboardArrowDownIcon}
          displayEmpty
          renderValue={(selected) => (
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
              {selected === 'buy' ? t('trade.buy') : t('trade.sell')}
            </Typography>
          )}
          MenuProps={{
            sx: { zIndex: 14000 },
            PaperProps: {
              sx: {
                mt: 0.5,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                minWidth: 100,
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
            color: 'text.primary',
            bgcolor: 'transparent',
            cursor: 'pointer',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiSelect-icon': {
              color: 'text.secondary',
              pointerEvents: 'none',
            },
          }}
        >
          <MenuItem
            value="buy"
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 600,
              py: 1.5,
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {t('trade.buy')}
          </MenuItem>
          <MenuItem
            value="sell"
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 600,
              py: 1.5,
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {t('trade.sell')}
          </MenuItem>
        </Select>
      </FormControl>

      {/* Market/Limit Toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          {orderType === 'limit' ? t('trade.limit') : t('trade.market')}
        </Typography>
        <Switch
          checked={orderType === 'limit'}
          onChange={(e) => onOrderTypeChange(e.target.checked ? 'limit' : 'market')}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#2563eb',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              bgcolor: '#2563eb',
            },
          }}
        />
      </Box>
    </Box>
  );
}
